import React, { useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  ImportOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SendOutlined,
  ThunderboltOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { AttendancePhysicalVerificationSheet } from '@/components/hr/AttendancePhysicalVerificationSheet';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { projectService } from '@/services/project.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';
import { openHrmsPrintOverlay } from './shared/openHrmsPrintOverlay';

const { Text } = Typography;

type DayFilter = 'all' | 'checked_in' | 'missing_checkin' | 'missing_checkout' | 'completed';

/** Statuses that explain no punch without treating the person as missing check-in. */
const EXCUSED_NO_PUNCH = new Set(['Leave', 'Holiday', 'WeeklyOff']);

function hasPunchTime(v: unknown): boolean {
  return typeof v === 'string' ? v.trim().length > 0 : Boolean(v);
}

function isCheckedIn(row: { check_in?: unknown }): boolean {
  return hasPunchTime(row.check_in);
}

function isMissingCheckout(row: { check_in?: unknown; check_out?: unknown }): boolean {
  return hasPunchTime(row.check_in) && !hasPunchTime(row.check_out);
}

function isCompletedOut(row: { check_in?: unknown; check_out?: unknown }): boolean {
  return hasPunchTime(row.check_in) && hasPunchTime(row.check_out);
}

function isExcusedNoPunch(row: { status?: unknown } | undefined): boolean {
  return EXCUSED_NO_PUNCH.has(String(row?.status || ''));
}

const PUNCHED_ATTENDANCE_SOURCES = new Set(['MobilePunch', 'MobileAutoPunch']);

function isPunchedAttendanceSource(source: unknown): boolean {
  return PUNCHED_ATTENDANCE_SOURCES.has(String(source || ''));
}

const AttendancePage: React.FC = () => {
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const canRun = Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.attendance.manage'));
  const canManageDays = canRun;
  const activeTab = searchParams.get('tab') === 'months' ? 'months' : 'days';
  const [date, setDate] = useState(dayjs());
  const [month, setMonth] = useState(() =>
    searchParams.get('month') ? dayjs(`${searchParams.get('month')}-01`) : dayjs()
  );
  const [monthStatus, setMonthStatus] = useState<string>();
  const [selectedMonthIds, setSelectedMonthIds] = useState<React.Key[]>([]);
  const [showDayForm, setShowDayForm] = useState(false);
  const [dayFormMode, setDayFormMode] = useState<'create' | 'edit'>('create');
  /** When editing a MobilePunch / MobileAutoPunch day, reason is required and source is preserved. */
  const [editingPunchedSource, setEditingPunchedSource] = useState<string | null>(null);
  const [dayFilter, setDayFilter] = useState<DayFilter>('all');
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importErrorsOpen, setImportErrorsOpen] = useState(false);
  const [form] = Form.useForm();
  const { options: empOptions, employees, empLabel } = useEmployeeOptions();
  const dayFormRef = React.useRef<HTMLDivElement | null>(null);

  const dayParams = { date: date.format('YYYY-MM-DD') };
  const { data: days = [], isLoading: daysLoading, refetch: refetchDays } = useQuery({
    queryKey: hrmsKeys.attendanceDays(dayParams),
    queryFn: () => hrmsService.listAttendanceDays(dayParams),
  });

  const empById = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    for (const e of employees) {
      m.set(e.id, {
        code: e.employee_code || '',
        name: `${e.first_name || ''} ${e.last_name || ''}`.trim(),
      });
    }
    return m;
  }, [employees]);

  const dayByEmployee = useMemo(() => {
    const m = new Map<string, any>();
    for (const row of days) {
      if (row?.employee_id) m.set(String(row.employee_id), row);
    }
    return m;
  }, [days]);

  /** Active employees expected at work with no In punch (excludes leave / holiday / weekly off). */
  const missingCheckInRows = useMemo(() => {
    const dateStr = date.format('YYYY-MM-DD');
    const rows: any[] = [];
    for (const e of employees) {
      const existing = dayByEmployee.get(e.id);
      if (existing && isCheckedIn(existing)) continue;
      if (isExcusedNoPunch(existing)) continue;
      rows.push({
        id: existing?.id || `missing-checkin-${e.id}`,
        employee_id: e.id,
        attendance_date: existing?.attendance_date || dateStr,
        status: existing?.status || 'NoRecord',
        check_in: existing?.check_in || null,
        check_out: existing?.check_out || null,
        worked_hours: existing?.worked_hours ?? 0,
        ot_hours: existing?.ot_hours ?? 0,
        late_minutes: existing?.late_minutes ?? 0,
        source: existing?.source || null,
        project_hours: existing?.project_hours || [],
        _missing_checkin: true,
        _synthetic: !existing,
      });
    }
    return rows.sort((a, b) => empLabel(a.employee_id).localeCompare(empLabel(b.employee_id)));
  }, [employees, dayByEmployee, date, empLabel]);

  const daySummary = useMemo(() => {
    let checkedIn = 0;
    let missingCheckout = 0;
    let completedOut = 0;
    let present = 0;
    let absent = 0;
    let leave = 0;
    let other = 0;
    for (const row of days) {
      if (isCheckedIn(row)) checkedIn += 1;
      if (isMissingCheckout(row)) missingCheckout += 1;
      if (isCompletedOut(row)) completedOut += 1;
      const status = String(row.status || '');
      if (status === 'Present' || status === 'HalfDay') present += 1;
      else if (status === 'Absent') absent += 1;
      else if (status === 'Leave') leave += 1;
      else other += 1;
    }
    return {
      total: days.length,
      checkedIn,
      missingCheckIn: missingCheckInRows.length,
      missingCheckout,
      completedOut,
      present,
      absent,
      leave,
      other,
      activeEmployees: employees.length,
    };
  }, [days, missingCheckInRows.length, employees.length]);

  const filteredDays = useMemo(() => {
    if (dayFilter === 'missing_checkin') return missingCheckInRows;

    let rows = days;
    if (dayFilter === 'checked_in') rows = days.filter(isCheckedIn);
    else if (dayFilter === 'missing_checkout') rows = days.filter(isMissingCheckout);
    else if (dayFilter === 'completed') rows = days.filter(isCompletedOut);

    return [...rows].sort((a, b) => {
      const am = isMissingCheckout(a) ? 0 : 1;
      const bm = isMissingCheckout(b) ? 0 : 1;
      if (am !== bm) return am - bm;
      return empLabel(a.employee_id).localeCompare(empLabel(b.employee_id));
    });
  }, [days, dayFilter, empLabel, missingCheckInRows]);

  const companyName = useMemo(() => {
    const memberships = user?.memberships || [];
    const match = memberships.find((m) => m.company_id === user?.company_id);
    return match?.name || memberships[0]?.name || '';
  }, [user]);

  const printMeta = () => ({
    company_name: companyName,
    attendance_date: date.format('DD-MMM-YYYY'),
    printed_at: dayjs().format('DD-MMM-YYYY HH:mm'),
    printed_by: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || '',
  });

  const mapPrintRow = (row: { employee_id: string; check_in?: unknown; source?: unknown; status?: unknown }) => {
    const emp = empById.get(row.employee_id);
    const label = empLabel(row.employee_id);
    const [codeFromLabel, ...nameParts] = label.split(' — ');
    return {
      employee_code: emp?.code || codeFromLabel || row.employee_id,
      employee_name: emp?.name || nameParts.join(' — ') || label,
      check_in: String(row.check_in || ''),
      source: String(row.source || 'Manual'),
      missing_checkout: isMissingCheckout(row),
      status: String(row.status || ''),
    };
  };

  const printPhysicalVerification = () => {
    const checkedInRows = days.filter(isCheckedIn);
    if (!checkedInRows.length) {
      message.warning('No checked-in employees for this date');
      return;
    }
    const rows = [...checkedInRows]
      .sort((a, b) => {
        const am = isMissingCheckout(a) ? 0 : 1;
        const bm = isMissingCheckout(b) ? 0 : 1;
        if (am !== bm) return am - bm;
        return empLabel(a.employee_id).localeCompare(empLabel(b.employee_id));
      })
      .map(mapPrintRow);
    openHrmsPrintOverlay((onClose) => (
      <AttendancePhysicalVerificationSheet
        onClose={onClose}
        variant="physical_verification"
        doc={{
          ...printMeta(),
          rows,
          summary: {
            checked_in: daySummary.checkedIn,
            missing_checkout: daySummary.missingCheckout,
            completed_out: daySummary.completedOut,
          },
        }}
      />
    ));
  };

  const printMissingCheckIn = () => {
    if (!missingCheckInRows.length) {
      message.warning('No missing check-ins for this date');
      return;
    }
    openHrmsPrintOverlay((onClose) => (
      <AttendancePhysicalVerificationSheet
        onClose={onClose}
        variant="missing_checkin"
        doc={{
          ...printMeta(),
          rows: missingCheckInRows.map(mapPrintRow),
          summary: {
            checked_in: daySummary.checkedIn,
            missing_checkout: daySummary.missingCheckout,
            completed_out: daySummary.completedOut,
            missing_checkin: daySummary.missingCheckIn,
            active_employees: daySummary.activeEmployees,
          },
        }}
      />
    ));
  };

  const monthParams = { year: month.year(), month: month.month() + 1, status: monthStatus };
  const { data: months = [], isLoading: monthsLoading } = useQuery({
    queryKey: hrmsKeys.attendanceMonths(monthParams),
    queryFn: () => hrmsService.listAttendanceMonths(monthParams),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'selection', 'attendance'],
    queryFn: () => projectService.getProjectsForSelection(),
    staleTime: 5 * 60 * 1000,
  });

  const projectOptions = useMemo(
    () =>
      (projects || []).map((p: any) => ({
        value: p.id,
        label: `${p.project_number || p.project_name || 'Project'} — ${p.project_name || ''}`.trim(),
        code: p.project_number || '',
      })),
    [projects]
  );

  const closeDayForm = () => {
    setShowDayForm(false);
    setDayFormMode('create');
    setEditingPunchedSource(null);
    form.resetFields();
    form.setFieldsValue({ attendance_date: date, status: 'Present', project_hours: [] });
  };

  const openCreateDay = () => {
    if (!canManageDays) {
      message.warning('You need attendance manage permission to edit the daily register');
      return;
    }
    setDayFormMode('create');
    setEditingPunchedSource(null);
    form.resetFields();
    form.setFieldsValue({ attendance_date: date, status: 'Present', project_hours: [] });
    setShowDayForm(true);
    setTimeout(() => dayFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const openEditDay = (row: any) => {
    if (!canManageDays) {
      message.warning('You need attendance manage permission to edit the daily register');
      return;
    }
    const status = row.status === 'NoRecord' || !row.status ? 'Present' : String(row.status);
    const punched = isPunchedAttendanceSource(row.source);
    setDayFormMode('edit');
    setEditingPunchedSource(punched ? String(row.source) : null);
    form.setFieldsValue({
      employee_id: row.employee_id,
      attendance_date: dayjs(row.attendance_date || date),
      status,
      check_in: row.check_in || undefined,
      check_out: row.check_out || undefined,
      worked_hours: row.worked_hours != null ? Number(row.worked_hours) : undefined,
      ot_hours: row.ot_hours != null ? Number(row.ot_hours) : undefined,
      late_minutes: row.late_minutes != null ? Number(row.late_minutes) : undefined,
      notes: row.notes || undefined,
      manual_edit_reason: undefined,
      project_hours: (row.project_hours || []).map((p: any) => ({
        project_id: p.project_id,
        hours: Number(p.hours || 0),
        cost_center_code: p.cost_center_code || undefined,
        notes: p.notes || undefined,
      })),
    });
    setShowDayForm(true);
    setTimeout(() => dayFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const saveDay = async () => {
    try {
      const v = await form.validateFields();
      const project_hours = (v.project_hours || [])
        .map((line: any) => {
          const opt = projectOptions.find((o) => o.value === line.project_id);
          return {
            project_id: line.project_id || undefined,
            project_code: opt?.code || line.project_code || undefined,
            hours: Number(line.hours || 0),
            cost_center_code: line.cost_center_code || undefined,
            notes: line.notes || undefined,
          };
        })
        .filter((line: any) => line.hours > 0);
      await hrmsService.upsertAttendanceDay({
        employee_id: v.employee_id,
        attendance_date: v.attendance_date.format('YYYY-MM-DD'),
        status: v.status,
        // Send null so cleared In/Out overwrite existing punches on edit (upsert treats undefined as keep).
        check_in: v.check_in ? String(v.check_in).trim() : null,
        check_out: v.check_out ? String(v.check_out).trim() : null,
        worked_hours: v.worked_hours,
        ot_hours: v.ot_hours,
        late_minutes: v.late_minutes,
        notes: v.notes ?? null,
        project_hours,
        ...(editingPunchedSource
          ? { manual_edit_reason: String(v.manual_edit_reason || '').trim() }
          : {}),
      });
      message.success(dayFormMode === 'edit' ? 'Attendance day updated' : 'Attendance day saved');
      closeDayForm();
      queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceDays(dayParams) });
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await hrmsService.exportAttendanceExcelBlob(dayParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-timesheet-${date.format('YYYY-MM-DD')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Export started');
    } catch {
      message.error('Export failed');
    }
  };

  const handleTemplate = async () => {
    try {
      await hrmsService.downloadAttendanceTemplateAsFile();
      message.success('Import template downloaded');
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Template download failed');
    }
  };

  const importMut = useMutation({
    mutationFn: (file: File) => hrmsService.importAttendanceExcel(file),
    onSuccess: (result) => {
      const { created, updated, skipped, errors } = result;
      message.success(
        `Import done: ${created} created, ${updated} updated` +
          (skipped ? `, ${skipped} skipped` : '') +
          (errors?.length ? `, ${errors.length} row error(s)` : '')
      );
      if (errors?.length) {
        setImportErrors(errors);
        setImportErrorsOpen(true);
      }
      void queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceDays(dayParams) });
      void queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceMonths() });
    },
    onError: (e: Error) => message.error(e.message || 'Import failed'),
  });

  const dayColumns: ColumnsType<any> = [
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    {
      title: 'Date',
      dataIndex: 'attendance_date',
      width: 120,
      render: (d: string) => dayjs(d).format('DD-MMM-YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) =>
        s === 'NoRecord' ? <Tag color="default">No record</Tag> : <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'In',
      dataIndex: 'check_in',
      width: 140,
      render: (v: string, r) => {
        if (v) return v;
        if (r._missing_checkin || (!hasPunchTime(v) && dayFilter === 'missing_checkin')) {
          return (
            <Tag color="red" style={{ marginInlineEnd: 0 }}>
              Missing check-in
            </Tag>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Out',
      dataIndex: 'check_out',
      width: 140,
      render: (v: string, r) => {
        if (v) return v;
        if (isMissingCheckout(r)) {
          return (
            <Tag color="orange" style={{ marginInlineEnd: 0 }}>
              Missing checkout
            </Tag>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    { title: 'Worked', dataIndex: 'worked_hours', width: 90 },
    { title: 'OT', dataIndex: 'ot_hours', width: 80 },
    { title: 'Late (min)', dataIndex: 'late_minutes', width: 90 },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 160,
      render: (s: string, r) => (
        <Space direction="vertical" size={0}>
          {s === 'AutoGenerated' ? (
            <Tag color="purple">Auto</Tag>
          ) : s === 'MobileAutoPunch' ? (
            <Tag color="cyan">Auto check-in</Tag>
          ) : s === 'MobilePunch' ? (
            <Tag color="blue">Mobile</Tag>
          ) : (
            <Tag>{s || 'Manual'}</Tag>
          )}
          {r.geofence_validation === 'bypassed' ? (
            <Tag color="gold" style={{ marginInlineEnd: 0 }}>
              Geofence exempted
            </Tag>
          ) : r.geofence_validation === 'validated' &&
            (s === 'MobilePunch' || s === 'MobileAutoPunch') ? (
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              Geofence validated
            </Tag>
          ) : null}
          {Number(r.is_manually_edited) === 1 ? (
            <Tooltip
              title={
                <span>
                  Edited by {r.manually_edited_by_name || 'user'}
                  {r.manually_edited_at ? ` · ${dayjs(r.manually_edited_at).format('DD-MMM-YYYY HH:mm')}` : ''}
                  {r.manual_edit_reason ? ` — ${r.manual_edit_reason}` : ''}
                </span>
              }
            >
              <Tag color="geekblue" style={{ marginInlineEnd: 0, cursor: 'help' }}>
                Edited · {r.manually_edited_by_name || 'user'}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Projects',
      key: 'projects',
      width: 220,
      render: (_, r) => {
        const lines = r.project_hours || [];
        if (!lines.length) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={0}>
            {lines.map((p: any) => (
              <Text key={p.id || `${p.project_id}-${p.hours}`} style={{ fontSize: 12 }}>
                {p.project_code || 'Project'} · {Number(p.hours || 0)}h
              </Text>
            ))}
          </Space>
        );
      },
    },
    ...(canManageDays
      ? ([
          {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 100,
            render: (_: unknown, r: any) => (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditDay(r)}
              >
                {r._synthetic || r.status === 'NoRecord' ? 'Record' : 'Edit'}
              </Button>
            ),
          },
        ] as ColumnsType<any>)
      : []),
  ];

  const refreshMonths = () => {
    void queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceMonths() });
    setSelectedMonthIds([]);
  };

  const consolidateMut = useMutation({
    mutationFn: () => hrmsService.consolidateAttendanceMonths({ year: month.year(), month: month.month() + 1 }),
    onSuccess: (r) => {
      message.success(`${r.updated} timesheet(s) recalculated from the daily register`);
      if (r.skipped.length) {
        modal.info({
          title: `${r.skipped.length} not recalculated`,
          content: (
            <ul style={{ paddingLeft: 18 }}>
              {r.skipped.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          ),
        });
      }
      refreshMonths();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Recalculate failed'),
  });

  const submitBulkMut = useMutation({
    mutationFn: () => hrmsService.submitAttendanceMonthsBulk(selectedMonthIds.map(String)),
    onSuccess: (r) => {
      message.success(`${r.submitted} timesheet(s) submitted`);
      if (r.errors.length) {
        modal.warning({
          title: `${r.errors.length} not submitted`,
          content: (
            <ul style={{ paddingLeft: 18 }}>
              {r.errors.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          ),
        });
      }
      refreshMonths();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Submit failed'),
  });

  const monthColumns: ColumnsType<any> = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.employee_name || empLabel(r.employee_id)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.employee_code}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Period',
      key: 'period',
      width: 90,
      render: (_, r) => dayjs(`${r.period_year}-${String(r.period_month).padStart(2, '0')}-01`).format('MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    { title: 'Present', dataIndex: 'days_present', width: 75, align: 'right', render: (v) => Number(v || 0) },
    { title: 'Leave', dataIndex: 'days_leave', width: 65, align: 'right', render: (v) => Number(v || 0) },
    { title: 'Holiday', dataIndex: 'days_holiday', width: 75, align: 'right' },
    { title: 'Off', dataIndex: 'days_weekly_off', width: 55, align: 'right' },
    {
      title: 'Absent',
      dataIndex: 'days_absent',
      width: 70,
      align: 'right',
      render: (v) => (Number(v) ? <Text type="danger">{Number(v)}</Text> : 0),
    },
    { title: 'Worked hrs', dataIndex: 'total_worked_hours', width: 95, align: 'right', render: (v) => Number(v || 0) },
    { title: 'OT', dataIndex: 'total_ot_hours', width: 60, align: 'right', render: (v) => Number(v || 0) },
    {
      title: 'Source',
      key: 'source',
      width: 130,
      render: (_, r) =>
        r.generated_at ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Auto · {dayjs(r.generated_at).format('DD MMM HH:mm')}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Daily register
          </Text>
        ),
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/hr/attendance/months/${r.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Attendance / Timesheets"
          subtitle="Daily attendance with project hours, Excel import/export, monthly consolidation"
          primaryActions={
            <Space wrap>
              <Button icon={<DownloadOutlined />} onClick={() => void handleExport()}>
                Export Excel
              </Button>
              <Button icon={<ImportOutlined />} onClick={() => void handleTemplate()}>
                Import template
              </Button>
              <Upload
                accept=".xlsx,.xls"
                showUploadList={false}
                beforeUpload={(file) => {
                  void importMut.mutateAsync(file);
                  return false;
                }}
              >
                <Button icon={<UploadOutlined />} loading={importMut.isPending}>
                  Import Excel
                </Button>
              </Upload>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="How attendance feeds payroll"
        description={
          <span>
            Capture daily Present / Absent / Leave / Holiday rows with optional <strong>project hours</strong> (for
            payroll project cost split). Download the Excel template, fill <code>employee_code</code> +{' '}
            <code>attendance_date</code> (+ <code>project_code</code> / <code>project_hours</code>), then import.
            Late minutes use shift grace on <Link to="/hr/setup">HRMS Setup</Link>.
          </span>
        }
      />
      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', k);
          setSearchParams(next, { replace: true });
        }}
        items={[
          {
            key: 'days',
            label: 'Daily register',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <DatePicker
                      value={date}
                      onChange={(d) => {
                        if (!d) return;
                        setDate(d);
                        setDayFilter('all');
                      }}
                    />
                    {canManageDays ? (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          if (showDayForm && dayFormMode === 'create') closeDayForm();
                          else openCreateDay();
                        }}
                      >
                        {showDayForm && dayFormMode === 'create' ? 'Hide add row' : 'Add day'}
                      </Button>
                    ) : null}
                    <Button icon={<ReloadOutlined />} onClick={() => void refetchDays()}>
                      Refresh
                    </Button>
                    <Button
                      icon={<PrinterOutlined />}
                      onClick={printPhysicalVerification}
                      disabled={!daySummary.checkedIn}
                    >
                      Print physical verification
                    </Button>
                    <Button
                      icon={<PrinterOutlined />}
                      onClick={printMissingCheckIn}
                      disabled={!daySummary.missingCheckIn}
                      danger={daySummary.missingCheckIn > 0}
                    >
                      Print missing check-in
                    </Button>
                    <Text type="secondary">
                      {filteredDays.length}
                      {dayFilter === 'missing_checkin'
                        ? ` of ${daySummary.activeEmployees} active`
                        : dayFilter !== 'all'
                          ? ` of ${days.length}`
                          : ''}{' '}
                      row(s)
                    </Text>
                  </Space>
                </Card>

                <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic title="Register rows" value={daySummary.total} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic title="Checked in" value={daySummary.checkedIn} valueStyle={{ color: '#1677ff' }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic
                        title="Missing check-in"
                        value={daySummary.missingCheckIn}
                        valueStyle={{ color: daySummary.missingCheckIn ? '#cf1322' : undefined }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic
                        title="Missing checkout"
                        value={daySummary.missingCheckout}
                        valueStyle={{ color: daySummary.missingCheckout ? '#d46b08' : undefined }}
                      />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic title="Completed out" value={daySummary.completedOut} valueStyle={{ color: '#389e0d' }} />
                    </Card>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                      <Statistic title="Active employees" value={daySummary.activeEmployees} />
                    </Card>
                  </Col>
                </Row>

                {daySummary.missingCheckIn > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${daySummary.missingCheckIn} active employee(s) have not checked in`}
                    description="Compared to active employees. Leave, holiday, and weekly-off rows are excluded. Print the list for floor follow-up, or use Add / upsert to record absence or a late punch."
                    action={
                      <Space>
                        <Button size="small" onClick={() => setDayFilter('missing_checkin')}>
                          Show list
                        </Button>
                        <Button size="small" icon={<PrinterOutlined />} onClick={printMissingCheckIn}>
                          Print
                        </Button>
                      </Space>
                    }
                  />
                )}

                {daySummary.missingCheckout > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`${daySummary.missingCheckout} employee(s) checked in without checkout`}
                    description="Filter to Missing checkout, complete Out via Add / upsert, or use Print physical verification to walk the floor."
                    action={
                      <Button size="small" onClick={() => setDayFilter('missing_checkout')}>
                        Show missing checkout
                      </Button>
                    }
                  />
                )}

                <Card size="small" style={{ marginBottom: 12 }}>
                  <Segmented
                    value={dayFilter}
                    onChange={(v) => setDayFilter(v as DayFilter)}
                    options={[
                      { label: `All (${daySummary.total})`, value: 'all' },
                      { label: `Checked in (${daySummary.checkedIn})`, value: 'checked_in' },
                      {
                        label: `Missing check-in (${daySummary.missingCheckIn})`,
                        value: 'missing_checkin',
                      },
                      {
                        label: `Missing checkout (${daySummary.missingCheckout})`,
                        value: 'missing_checkout',
                      },
                      { label: `Completed (${daySummary.completedOut})`, value: 'completed' },
                    ]}
                  />
                </Card>

                {showDayForm && (
                  <div ref={dayFormRef}>
                    <Card
                      title={dayFormMode === 'edit' ? 'Edit attendance day' : 'Add attendance day'}
                      size="small"
                      style={{ marginBottom: 16 }}
                      extra={
                        <Button type="link" onClick={closeDayForm}>
                          Cancel
                        </Button>
                      }
                    >
                      <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ attendance_date: date, status: 'Present', project_hours: [] }}
                        onFinish={saveDay}
                      >
                        <Space wrap size="middle" style={{ width: '100%' }}>
                          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                            <Select
                              placeholder="Employee"
                              style={{ width: 240 }}
                              options={empOptions}
                              showSearch
                              optionFilterProp="label"
                              disabled={dayFormMode === 'edit'}
                            />
                          </Form.Item>
                          <Form.Item name="attendance_date" label="Date" rules={[{ required: true }]}>
                            <DatePicker disabled={dayFormMode === 'edit'} />
                          </Form.Item>
                          <Form.Item name="status" label="Status">
                            <Select
                              style={{ width: 160 }}
                              options={[
                                { value: 'Present', label: 'Present' },
                                { value: 'Absent', label: 'Absent (unpaid)' },
                                { value: 'Leave', label: 'On approved leave' },
                                { value: 'Holiday', label: 'Public / company holiday' },
                                { value: 'WeeklyOff', label: 'Weekly off' },
                                { value: 'HalfDay', label: 'Half day' },
                              ]}
                            />
                          </Form.Item>
                          <Form.Item name="check_in" label="In">
                            <Input placeholder="HH:mm or HH:mm:ss" style={{ width: 130 }} />
                          </Form.Item>
                          <Form.Item name="check_out" label="Out">
                            <Input placeholder="HH:mm or HH:mm:ss" style={{ width: 130 }} />
                          </Form.Item>
                          <Form.Item name="worked_hours" label="Worked hrs">
                            <InputNumber min={0} step={0.25} />
                          </Form.Item>
                          <Form.Item name="ot_hours" label="OT hrs">
                            <InputNumber min={0} step={0.25} />
                          </Form.Item>
                          <Form.Item name="late_minutes" label="Late (min)">
                            <InputNumber min={0} step={1} />
                          </Form.Item>
                        </Space>
                        <Form.Item name="notes" label="Notes">
                          <Input.TextArea rows={1} />
                        </Form.Item>
                        {editingPunchedSource ? (
                          <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`This day was pushed from ${editingPunchedSource}`}
                            description="Manual changes are audited. Enter why you are correcting the punch data. The original source stays MobilePunch / MobileAutoPunch."
                          />
                        ) : null}
                        {editingPunchedSource ? (
                          <Form.Item
                            name="manual_edit_reason"
                            label="Edit reason"
                            rules={[
                              { required: true, message: 'Reason is required when editing punched attendance' },
                              { min: 3, message: 'Enter at least 3 characters' },
                            ]}
                          >
                            <Input.TextArea
                              rows={2}
                              placeholder="e.g. Employee forgot to check out — confirmed with supervisor"
                              maxLength={500}
                              showCount
                            />
                          </Form.Item>
                        ) : null}
                        <Form.List name="project_hours">
                          {(fields, { add, remove }) => (
                            <>
                              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                Project hours
                              </Text>
                              {fields.map((field) => (
                                <Space key={field.key} align="start" wrap style={{ marginBottom: 8 }}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'project_id']}
                                    rules={[{ required: true, message: 'Project' }]}
                                  >
                                    <Select
                                      placeholder="Project"
                                      style={{ width: 280 }}
                                      options={projectOptions}
                                      showSearch
                                      optionFilterProp="label"
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'hours']}
                                    rules={[{ required: true, message: 'Hours' }]}
                                  >
                                    <InputNumber min={0.25} step={0.25} placeholder="Hours" />
                                  </Form.Item>
                                  <Form.Item {...field} name={[field.name, 'cost_center_code']}>
                                    <Input placeholder="Cost center" style={{ width: 120 }} />
                                  </Form.Item>
                                  <Form.Item {...field} name={[field.name, 'notes']}>
                                    <Input placeholder="Notes" style={{ width: 160 }} />
                                  </Form.Item>
                                  <Button type="link" danger onClick={() => remove(field.name)}>
                                    Remove
                                  </Button>
                                </Space>
                              ))}
                              <Button type="dashed" onClick={() => add({ hours: 8 })} icon={<PlusOutlined />}>
                                Add project line
                              </Button>
                            </>
                          )}
                        </Form.List>
                        <div style={{ marginTop: 16 }}>
                          <Space>
                            <Button type="primary" htmlType="submit">
                              {dayFormMode === 'edit' ? 'Update day' : 'Save day'}
                            </Button>
                            <Button onClick={closeDayForm}>Cancel</Button>
                          </Space>
                        </div>
                      </Form>
                    </Card>
                  </div>
                )}
                <Table
                  rowKey="id"
                  loading={daysLoading}
                  columns={dayColumns}
                  dataSource={filteredDays}
                  pagination={{ pageSize: 20 }}
                  scroll={{ x: 1200 }}
                  locale={{
                    emptyText: (
                      <Empty
                        description={
                          dayFilter === 'missing_checkin'
                            ? 'All active employees have checked in (or are on leave / holiday / weekly off)'
                            : dayFilter === 'missing_checkout'
                              ? 'No missing checkouts for this date'
                              : dayFilter === 'checked_in'
                                ? 'No checked-in employees for this date'
                                : dayFilter === 'completed'
                                  ? 'No completed check-outs for this date'
                                  : 'No attendance rows for this date'
                        }
                      />
                    ),
                  }}
                  onRow={(r) => {
                    if (r._missing_checkin || dayFilter === 'missing_checkin') {
                      return { style: { background: 'rgba(255, 77, 79, 0.06)' } };
                    }
                    if (isMissingCheckout(r)) {
                      return { style: { background: 'rgba(250, 173, 20, 0.08)' } };
                    }
                    return {};
                  }}
                />
              </>
            ),
          },
          {
            key: 'months',
            label: 'Monthly timesheets',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space wrap>
                    <DatePicker
                      picker="month"
                      value={month}
                      allowClear={false}
                      format="MMM YYYY"
                      onChange={(d) => {
                        if (!d) return;
                        setMonth(d);
                        setSelectedMonthIds([]);
                      }}
                    />
                    <Select
                      placeholder="All statuses"
                      allowClear
                      style={{ width: 170 }}
                      value={monthStatus}
                      onChange={setMonthStatus}
                      options={['Draft', 'PendingApproval', 'Submitted', 'Approved', 'Rejected'].map((s) => ({
                        value: s,
                        label: s === 'PendingApproval' ? 'Pending approval' : s,
                      }))}
                    />
                    <Text type="secondary">{months.length} timesheet(s)</Text>
                    {canRun && (
                      <>
                        <Button
                          type="primary"
                          icon={<ThunderboltOutlined />}
                          onClick={() => navigate(`/hr/attendance/generate?month=${month.format('YYYY-MM')}`)}
                        >
                          Auto-generate
                        </Button>
                        <Button
                          icon={<ReloadOutlined />}
                          loading={consolidateMut.isPending}
                          onClick={() =>
                            modal.confirm({
                              title: `Recalculate ${month.format('MMM YYYY')} timesheets from the daily register?`,
                              content:
                                'Creates or refreshes the monthly totals for every employee with attendance days in this month. Submitted / approved timesheets are not changed; rejected ones return to Draft.',
                              onOk: () => consolidateMut.mutateAsync(),
                            })
                          }
                        >
                          Recalculate from daily register
                        </Button>
                        <Button
                          icon={<SendOutlined />}
                          disabled={!selectedMonthIds.length}
                          loading={submitBulkMut.isPending}
                          onClick={() =>
                            modal.confirm({
                              title: `Submit ${selectedMonthIds.length} timesheet(s) for approval?`,
                              onOk: () => submitBulkMut.mutateAsync(),
                            })
                          }
                        >
                          Submit selected{selectedMonthIds.length ? ` (${selectedMonthIds.length})` : ''}
                        </Button>
                      </>
                    )}
                  </Space>
                </Card>
                <Table
                  rowKey="id"
                  loading={monthsLoading}
                  columns={monthColumns}
                  dataSource={months}
                  scroll={{ x: 1100 }}
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
                  rowSelection={
                    canRun
                      ? {
                          selectedRowKeys: selectedMonthIds,
                          onChange: setSelectedMonthIds,
                          getCheckboxProps: (r: any) => ({ disabled: r.status !== 'Draft' }),
                        }
                      : undefined
                  }
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={`No timesheets for ${month.format('MMM YYYY')}. Use Auto-generate to build them.`}
                      />
                    ),
                  }}
                  onRow={(r) => ({
                    onClick: (e) => {
                      if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
                      navigate(`/hr/attendance/months/${r.id}`);
                    },
                    style: { cursor: 'pointer' },
                  })}
                />
              </>
            ),
          },
        ]}
      />

      <Modal
        title="Import row errors"
        open={importErrorsOpen}
        onCancel={() => setImportErrorsOpen(false)}
        onOk={() => setImportErrorsOpen(false)}
        width={720}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Some rows were not applied. Fix the Excel file and re-import."
        />
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {importErrors.map((err, i) => (
            <Text key={i} style={{ display: 'block', marginBottom: 6 }}>
              {err}
            </Text>
          ))}
        </div>
      </Modal>
    </WorkspaceLayout>
  );
};

export default AttendancePage;
